// View helpers and the poll controller of the AI Optimise tab on the strategies page. Plain CommonJS (it may require
// lodash and the pure label map only, no React), so it can be tested in plain node -- see src/lib/action-outcome.js and test/ai-optimization.js.
const { omit, isEqual } = require('lodash');
const { changeLabel, formatValue, getAtPointer } = require('./strategy-change-labels');

// Nothing here computes a profit of its own: every number shown comes from the stored run row, and a progress field the
// daemon has not written yet (null) is shown as "not available", never as 0.

const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE = 25;
const MAX_LOADED_RUNS = 100;
const ACTIVE_STATUSES = ['queued', 'running'];

const STATUS_LABELS = {
	queued: 'Queued',
	running: 'Running',
	done: 'Done',
	not_found: 'Not found',
	failed: 'Failed',
	cancelled: 'Cancelled',
};

// Tailwind JIT needs every class as a whole literal string: a lookup object, never a template-built class name
const STATUS_PILL_CLASSES = {
	queued: 'bg-sky-700',
	running: 'bg-indigo-600',
	done: 'bg-green-600',
	not_found: 'bg-amber-600',
	failed: 'bg-red-600',
	cancelled: 'bg-gray-600',
};

const PILL_BASE_CLASS = 'py-1 px-2 rounded-full text-center text-white whitespace-nowrap text-xs';

// the keys the strategies page rewrites by itself (results of a simulation): they are not an edit of the strategy
const RESULT_KEYS = ['strategyresult', 'tradesresult', 'tradesresultscore', 'maturity'];

const FINISH_REASON_LABELS = {
	time_budget: 'time budget used',
	max_rounds: 'all rounds used',
	search_exhausted: 'the AI had nothing new to try',
	cancelled: 'cancelled',
	ai_error: 'the AI advisor failed',
	engine_error: 'engine error',
	calibration_failed: 'too slow to search within the budget',
	window_error: 'the candle window could not be loaded',
	owner_lost: 'the simulator stopped reporting',
	interrupted: 'interrupted twice, not re-queued',
	strategy_missing: 'the strategy no longer exists',
	internal: 'internal error',
};

const BADGE_CLASSES = { applied: 'bg-green-800', outdated: 'bg-gray-700' };

const FAILED_TEXT_MAX = 80;

const CANCEL_RUNNING_CONFIRM = 'Cancel this running AI run? The search so far is discarded and nothing is applied.';

const UNSAVED_EDITS_TOAST = 'The run uses the saved strategy: your unsaved edits are not part of it.';

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const isNumber = value => typeof value === 'number' && Number.isFinite(value);

// The stop-loss (in %) an AI run searches under, chosen next to Start. The list exists in three repos and must stay equal:
// trading-api src/lib/ai-optimizations.js AI_RUN_STOPLOSS_CHOICES (the authority) and the daemon's src/optimizer/registry.rs
// RUN_STOPLOSS_CHOICES; test/ai-optimization.js checks all three. trading-api refuses any other value with HTTP 400
const RUN_STOPLOSS_CHOICES = Object.freeze([5, 8, 10, 12, 15, 20]);
const DEFAULT_RUN_STOPLOSS = 10;

// A select hands over text ('15'), trading-api wants the number 15: only a choice of the list comes out, else null
const parseRunStoploss = value => {
	let number = null;
	if (isNumber(value)) {
		number = value;
	} else if (typeof value === 'string' && value.trim() !== '') {
		number = Number(value);
	}
	return isNumber(number) && RUN_STOPLOSS_CHOICES.includes(number) ? number : null;
};

// 15 -> '15%', 3.3 -> '3.3%' (no space before %, no float noise); null for anything that is not a number
const formatPercent = value => {
	if (!isNumber(value)) {
		return null;
	}
	return `${Math.round(value * 10000) / 10000}%`;
};

// The toast after a successful Start: the stop-loss the server stored (echoed), else the one that was sent
const startToastText = ({ echoed, sent } = {}) => {
	let value = null;
	if (isNumber(echoed)) {
		value = echoed;
	} else if (isNumber(sent)) {
		value = sent;
	}
	return value === null ? 'AI optimise run queued' : `AI optimise run queued at ${formatPercent(value)} stop-loss`;
};

// A run before trading-api 0.8.0 searched the fixed 8 / 8.5 / 9 % grid and has no stop-loss of its own
const V10_GRID_LABEL = '8–9% (v1.0 grid)';

// The run's stop-loss is a number exactly when it is a v1.1 run: null, a missing key and any non-number mean v1.0
// (trading-api sends runstoploss: null for v1.0 rows, and applies the same rule)
const runStoploss = run => (isNumber(run?.runstoploss) ? run.runstoploss : null);

const runStoplossLabel = run => {
	const value = runStoploss(run);
	return value === null ? V10_GRID_LABEL : formatPercent(value);
};

// The run list's Stop-loss column is short: a v1.0 run reads a plain '8–9%'. The detail header keeps the longer label
// above, which says why that range is shown
const V10_LIST_LABEL = '8–9%';

const runStoplossListLabel = run => {
	const value = runStoploss(run);
	return value === null ? V10_LIST_LABEL : formatPercent(value);
};

// Plain decimal text only ('3.4', '15', '3.40'): Number() alone would also read '', '1e1' and '0x10' as numbers
const NUMERIC_TEXT = /^\s*\d+(\.\d+)?\s*$/;

// The stop-loss of a strategy as a number of at least 0: a JS number, or the numeric text a Postgres numeric column hands
// out. Anything else (missing, null, NaN, negative, other text) is unknown: null, never an invented value
const strategyStoplossValue = value => {
	let number = null;
	if (isNumber(value)) {
		number = value;
	} else if (typeof value === 'string' && NUMERIC_TEXT.test(value)) {
		number = Number(value);
	}
	return isNumber(number) && number >= 0 ? number : null;
};

// The stop-loss of a strategy: 0 is off in the engine; anything that is unknown (see strategyStoplossValue) is left out
const strategyStoplossText = value => {
	const number = strategyStoplossValue(value);
	if (number === null) {
		return null;
	}
	return number === 0 ? 'off' : formatPercent(number);
};

// Next to the Start dropdown: the stop-loss the saved strategy has itself (never the form's edited copy)
const startHint = savedStrategy => {
	const text = strategyStoplossText(savedStrategy?.stoplosspercent);
	return text === null ? null : `strategy's own: ${text}`;
};

// The detail header: the run's stop-loss and, for a v1.1 run that stored it, the strategy's own when the run started.
// Read from the run's metadata only, never from the strategy as it is now
const runStoplossHeaderText = run => {
	const head = `stop-loss ${runStoplossLabel(run)}`;
	if (runStoploss(run) === null) {
		return head;
	}
	const had = strategyStoplossText(run?.metadata?.strategyStoploss);
	return had === null ? head : `${head} (strategy had ${had} when the run started)`;
};

// A running run whose cancel the daemon has not confirmed yet reads 'Cancelling…'; an unknown status shows as it is
const statusView = run => {
	const status = run?.status;
	if (status === 'running' && run.cancelrequested) {
		return { label: 'Cancelling…', pillClass: STATUS_PILL_CLASSES.running };
	}
	if (has(STATUS_LABELS, status)) {
		return { label: STATUS_LABELS[status], pillClass: STATUS_PILL_CLASSES[status] };
	}
	return { label: String(status ?? ''), pillClass: 'bg-gray-600' };
};

const isActiveRun = run => ACTIVE_STATUSES.includes(run?.status);

const hasActiveRun = runs => Array.isArray(runs) && runs.some(isActiveRun);

// Profits are compared in integer cents: the stored profits have 2 decimals and float noise (0.1 + 0.2) must not matter
const profitCents = value => {
	if (value === null || value === undefined) {
		return null;
	}
	const number = parseFloat(value);
	if (!Number.isFinite(number)) {
		return null;
	}
	return Math.round(number * 100);
};

const formatProfit = value => {
	const cents = profitCents(value);
	if (cents === null) {
		return null;
	}
	return (cents / 100).toFixed(2);
};

// 375 -> '06:15', 6000 -> '100:00' (minutes keep counting past 60, a run can overrun its budget)
const formatClock = sec => {
	if (!isNumber(sec)) {
		return null;
	}
	const total = Math.max(0, Math.floor(sec));
	const minutes = String(Math.floor(total / 60)).padStart(2, '0');
	const seconds = String(total % 60).padStart(2, '0');
	return `${minutes}:${seconds}`;
};

// 3600 -> '60 mins', 90 -> '2 mins' (rounded up, so the limit is never understated), 60 -> '1 min'
const formatMaxMinutes = sec => {
	if (!isNumber(sec)) {
		return null;
	}
	const minutes = Math.ceil(Math.max(0, sec) / 60);
	return minutes === 1 ? '1 min' : `${minutes} mins`;
};

const progressView = run => {
	const { currentround, maxrounds, elapsedsec, budgetsec, evaluations, requeues } = run || {};
	const best = formatProfit(run?.bestdailyprofit);
	const baseline = formatProfit(run?.baselinedailyprofit);

	// The bar fills by round, like the "round N of M" text above it; before round 1 it is empty
	let fraction = null;
	if (isNumber(maxrounds) && maxrounds > 0) {
		const round = isNumber(currentround) ? currentround : 0;
		fraction = Math.min(1, Math.max(0, round / maxrounds));
	}

	let requeuedText = null;
	if (run?.status === 'queued' && isNumber(requeues) && requeues >= 1) {
		requeuedText = 're-queued after an interruption';
	}

	return {
		roundText:
			isNumber(currentround) && isNumber(maxrounds) ? `round ${currentround} of ${maxrounds}` : 'not started yet',
		elapsedText:
			isNumber(elapsedsec) && isNumber(budgetsec)
				? `${formatClock(elapsedsec)} of max ${formatMaxMinutes(budgetsec)}`
				: null,
		fraction,
		bestText: best === null ? 'no qualifier yet' : `${best}/day`,
		baselineText: baseline === null ? 'baseline not measured yet' : `${baseline}/day`,
		evaluationsText: isNumber(evaluations) ? `${evaluations.toLocaleString('en-US')} candidates evaluated` : null,
		requeuedText,
	};
};

// Newest created first, then id descending when two created values are equal: the server's own order
const compareRuns = (a, b) => {
	const byCreated = (Date.parse(b?.created) || 0) - (Date.parse(a?.created) || 0);
	if (byCreated !== 0) {
		return byCreated;
	}
	const idA = String(a?.id ?? '');
	const idB = String(b?.id ?? '');
	if (idA === idB) {
		return 0;
	}
	return idA < idB ? 1 : -1;
};

// The refreshed first page replaces the loaded runs with the same id; older loaded runs that are not in it stay
const mergeFirstPage = (loaded, page) => {
	const byId = new Map();
	(Array.isArray(loaded) ? loaded : []).forEach(run => {
		byId.set(run.id, run);
	});
	(Array.isArray(page) ? page : []).forEach(run => {
		byId.set(run.id, run);
	});
	return Array.from(byId.values()).sort(compareRuns).slice(0, MAX_LOADED_RUNS);
};

// Does the form hold edits of the saved strategy that the run (which uses the saved one) does not include?
const unsavedEdits = (form, saved) => {
	if (!form || !saved || !form.id || form.id !== saved.id) {
		return false;
	}
	return !isEqual(omit(form, RESULT_KEYS), omit(saved, RESULT_KEYS));
};

// 'Load more' page: older runs go after the loaded ones. A run that is already loaded (a new run shifted the offsets)
// stays once, with the loaded -- fresher -- values
const appendRunPage = (loaded, page) => {
	const byId = new Map();
	(Array.isArray(page) ? page : []).forEach(run => {
		byId.set(run.id, run);
	});
	(Array.isArray(loaded) ? loaded : []).forEach(run => {
		byId.set(run.id, run);
	});
	return Array.from(byId.values()).sort(compareRuns).slice(0, MAX_LOADED_RUNS);
};

// 'Load more' is offered after a full page only, and while fewer than MAX_LOADED_RUNS runs are held
const hasMorePages = (lastPageLength, loadedCount) => lastPageLength === PAGE_SIZE && loadedCount < MAX_LOADED_RUNS;

// Never blank: a reason this tab has no label for is shown as the code the server sent
const finishReasonText = code => {
	if (has(FINISH_REASON_LABELS, code)) {
		return FINISH_REASON_LABELS[code];
	}
	return code ? String(code) : 'reason not stored';
};

// How a finished run ended, for the Progress column; null while the run is still queued or running
const outcomeText = run => {
	const status = run?.status;
	if (status === 'done' || status === 'not_found') {
		return `${STATUS_LABELS[status]} · ${finishReasonText(run.finishreason)}`;
	}
	if (status === 'failed') {
		const error = String(run.error ?? '').trim();
		if (!error) {
			return finishReasonText(run.finishreason);
		}
		return error.length > FAILED_TEXT_MAX ? `${error.slice(0, FAILED_TEXT_MAX - 1)}…` : error;
	}
	if (status === 'cancelled') {
		return 'Cancelled';
	}
	return null;
};

// The history pills of a finished row: applied, or outdated because the strategy changed since the run
const rowBadges = run => {
	if (run?.appliableReason === 'already_applied' || run?.appliedat) {
		return ['applied'];
	}
	if (run?.appliableReason === 'strategy_changed') {
		return ['outdated'];
	}
	return [];
};

// Changes when the saved strategy's active flag or engine settings change, so the run list is re-read (appliable
// follows them); results of a simulation are left out
const appliabilitySignature = saved => {
	if (!saved) {
		return '';
	}
	return JSON.stringify({ active: !!saved.active, config: omit(saved, RESULT_KEYS) });
};

// Cancelling a running run discards a paid search, so it asks first; a queued run is cancelled at once
const cancelNeedsConfirm = run => run?.status === 'running';

// The one owner of the 5 s poll timer of a mounted panel. sync(true) makes sure exactly one timer runs, sync(false)
// cancels it, stop() cancels it for good: after stop() every sync is a no-op and every sequence number is stale, so a
// fetch that resolves late can neither apply its answer nor start a new timer. begin() hands out a sequence number
// per fetch; isCurrent(seq) is true only for the newest one.
const createRunPoller = ({ tick, schedule, intervalMs }) => {
	let timer = null;
	let stopped = false;
	let sequence = 0;

	const cancelTimer = () => {
		if (timer) {
			timer.cancel();
			timer = null;
		}
	};

	return {
		sync(active) {
			if (stopped) {
				return;
			}
			if (active) {
				if (!timer) {
					timer = schedule(tick, intervalMs, intervalMs);
				}
			} else {
				cancelTimer();
			}
		},
		stop() {
			stopped = true;
			sequence += 1;
			cancelTimer();
		},
		begin() {
			sequence += 1;
			return sequence;
		},
		isCurrent(seq) {
			return !stopped && seq === sequence;
		},
		isScheduled() {
			return timer !== null;
		},
	};
};

// ---- the detail of one run: the verdict ----

const PASS_MARK_LABELS = {
	zeroStoploss: '0 stop-losses',
	zeroBadTrades: '0 bad trades',
	goodTrades: 'at least 20 good trades',
	beatsBaseline: 'beats the baseline (compared at 2 decimals)',
	neighbourCheck: 'checked by the engine (RSI / Stochastic RSI in Enter)',
};

const MIN_GOOD_TRADES = 20;

// The five must-haves of a suggestion against the stored numbers. The neighbour check is not stored as a field: a done
// run passed it by construction (a qualifier that fails it is never the winner), so that mark is always a pass
const passMarks = ({ stats, baseline }) => {
	const suggested = profitCents(stats?.dailyProfit);
	const base = profitCents(baseline?.stats?.dailyProfit);
	const passes = {
		zeroStoploss: isNumber(stats?.stoplossCount) && stats.stoplossCount === 0,
		zeroBadTrades: isNumber(stats?.badTrades) && stats.badTrades === 0,
		goodTrades: isNumber(stats?.goodTrades) && stats.goodTrades >= MIN_GOOD_TRADES,
		beatsBaseline: suggested !== null && base !== null && suggested > base,
		neighbourCheck: true,
	};
	return Object.keys(PASS_MARK_LABELS).map(id => ({
		id,
		label: PASS_MARK_LABELS[id],
		pass: passes[id],
		text: PASS_MARK_LABELS[id],
	}));
};

const DASH = '—';

const countText = value => (isNumber(value) ? String(value) : DASH);

const profitText = value => formatProfit(value) ?? DASH;

// The rows of the Baseline (re-run) | Suggested | Must-have table; mark is the id of the pass mark of a row, or null
const statsRows = ({ stats, baseline }) => {
	const base = baseline?.stats;
	const dailyText = value => {
		const text = formatProfit(value);
		return text === null ? DASH : `${text}/day`;
	};
	return [
		{
			key: 'dailyProfit',
			label: 'Daily profit',
			baseline: dailyText(base?.dailyProfit),
			suggested: dailyText(stats?.dailyProfit),
			mark: 'beatsBaseline',
		},
		{
			key: 'totalProfit',
			label: 'Total profit',
			baseline: profitText(base?.totalProfit),
			suggested: profitText(stats?.totalProfit),
			mark: null,
		},
		{
			key: 'totalDays',
			label: 'Days',
			baseline: countText(base?.totalDays),
			suggested: countText(stats?.totalDays),
			mark: null,
		},
		{
			key: 'goodTrades',
			label: 'Good trades',
			baseline: countText(base?.goodTrades),
			suggested: countText(stats?.goodTrades),
			mark: 'goodTrades',
		},
		{
			key: 'badTrades',
			label: 'Bad trades',
			baseline: countText(base?.badTrades),
			suggested: countText(stats?.badTrades),
			mark: 'zeroBadTrades',
		},
		{
			key: 'stoplossCount',
			label: 'Stop-losses',
			baseline: countText(base?.stoplossCount),
			suggested: countText(stats?.stoplossCount),
			mark: 'zeroStoploss',
		},
		{
			key: 'profitTrailingCount',
			label: 'Profit-trailing closes',
			baseline: countText(base?.profitTrailingCount),
			suggested: countText(stats?.profitTrailingCount),
			mark: null,
		},
	];
};

// null without a stability check (only a done run has one)
const stabilityText = stability => {
	if (
		!stability ||
		typeof stability !== 'object' ||
		!isNumber(stability.neighbours) ||
		!isNumber(stability.qualifying)
	) {
		return null;
	}
	const text = `${stability.qualifying} of ${stability.neighbours} neighbours still qualify`;
	if (stability.complete === false) {
		const tested = isNumber(stability.evaluated) ? stability.evaluated : '?';
		return `${text} (check incomplete: ${tested} of ${stability.neighbours} tested)`;
	}
	return text;
};

// UTC milliseconds as 'YYYY-MM-DD HH:mm' (the engine pins its candle window to 00:00 UTC)
const utcText = ms => {
	if (!isNumber(ms)) {
		return null;
	}
	const date = new Date(ms);
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return date.toISOString().slice(0, 16).replace('T', ' ');
};

// 'NVDA 1440 min: <first> – <last> UTC, 6000 candles, cutoff <cutoff> UTC'; null without a window
const windowText = window => {
	if (!window || typeof window !== 'object') {
		return null;
	}
	const head = [window.asset, isNumber(window.intervalMin) ? `${window.intervalMin} min` : null]
		.filter(Boolean)
		.join(' ');
	const parts = [];
	const first = utcText(window.firstMs);
	const last = utcText(window.lastMs);
	if (first && last) {
		parts.push(`${first} – ${last} UTC`);
	}
	if (isNumber(window.count)) {
		parts.push(`${window.count} candles`);
	}
	const cutoff = utcText(window.cutoffMs);
	if (cutoff) {
		parts.push(`cutoff ${cutoff} UTC`);
	}
	if (parts.length === 0) {
		return head || null;
	}
	return head ? `${head}: ${parts.join(', ')}` : parts.join(', ');
};

const baselineNote = run => {
	const text = windowText(run?.metadata?.window ?? run?.baseline?.window);
	const stoploss = runStoploss(run);
	const withStoploss = stoploss === null ? '' : ` with stop-loss ${formatPercent(stoploss)}`;
	return `Baseline = the strategy's settings${withStoploss} re-run on the search window${text ? ` (${text})` : ''}.`;
};

const EARLIER_RULE_NOTE =
	'This run was found under the earlier rule, which allowed bad trades. Check the bad trades above before you apply it.';

// A done run that still has bad trades was found before the 0 bad trades rule. Read from the run's own stats only (no
// engine version, no server field): the run stays appliable, the user decides after seeing the cross and this note
const earlierRuleNote = run => {
	if (run?.status === 'done' && isNumber(run.stats?.badTrades) && run.stats.badTrades > 0) {
		return EARLIER_RULE_NOTE;
	}
	return null;
};

const NO_STORED_RESULT =
	'The strategy has no stored daily result right now (it is recomputed after a Save or an Apply).';

// The stored daily result of the saved strategy comes from the daily window and may differ from the baseline
const storedResultNote = savedStrategy => {
	const daily = formatProfit(savedStrategy?.strategyresult?.result?.dailyProfit);
	if (daily === null) {
		return NO_STORED_RESULT;
	}
	return `The strategy's stored daily result is ${daily}/day. It comes from the daily window and may differ from the baseline above.`;
};

// Fixed text next to Apply (UI-06): the suggestion was searched and judged on one window of candles only
const HYPOTHESIS_TEXT =
	'This suggestion was found and judged on the same candle window it was searched on. Treat it as a hypothesis: it has not been tested on other candles.';

// Why a done run cannot be applied (the appliableReason of the server)
const APPLIABLE_REASON_TEXTS = {
	strategy_active: 'Disable for Trading first',
	strategy_changed: 'Strategy changed after this run started — outdated',
	already_applied: 'Applied on',
	strategy_missing: 'The strategy no longer exists',
};

// null when there is nothing to explain: an appliable run, or a run that is not done (it has no Apply at all)
const appliableText = (run, formatDate) => {
	if (!run || run.status !== 'done' || run.appliable === true) {
		return null;
	}
	const reason = run.appliableReason;
	if (reason === 'already_applied') {
		const when = run.appliedat && typeof formatDate === 'function' ? formatDate(run.appliedat) : null;
		return when ? `${APPLIABLE_REASON_TEXTS.already_applied} ${when}` : 'Already applied';
	}
	if (!reason) {
		return 'Apply is not available for this run';
	}
	return APPLIABLE_REASON_TEXTS[reason] ?? String(reason);
};

const applyConfirmText = ({ changeCount, strategyName } = {}) => {
	const count = isNumber(changeCount) ? changeCount : 0;
	const name = typeof strategyName === 'string' && strategyName.trim() ? strategyName.trim() : 'this strategy';
	return `Apply ${count} changed ${
		count === 1 ? 'setting' : 'settings'
	} to ${name}? The strategy result is cleared and recomputed.`;
};

// What the Apply dialog adds under its question (APPLY-06): a v1.1 run (a numeric runstoploss) offers an unchecked
// "also set stop-loss to X%" option, shown with the stop-loss the saved strategy has now when that is a number; a
// strategy that already has the run's stop-loss (strict equality, the rule trading-api skips the change by) gets a
// line instead of the option; a run without its own stop-loss (v1.0) gets nothing and applies exactly as before.
// kind: none | already (text) | checkbox (label)
const applyCheckboxView = ({ run, savedStrategy } = {}) => {
	const runStop = runStoploss(run);
	if (runStop === null) {
		return { kind: 'none' };
	}
	const own = strategyStoplossValue(savedStrategy?.stoplosspercent);
	if (own !== null && own === runStop) {
		return { kind: 'already', text: `The strategy's stop-loss is already ${formatPercent(runStop)}.` };
	}
	const ownText = strategyStoplossText(own);
	const label = `also set stop-loss to ${formatPercent(runStop)}`;
	return { kind: 'checkbox', label: ownText === null ? label : `${label} (strategy now: ${ownText})` };
};

// The request body of an Apply: the run id, plus applystoploss true only for a strict true from a ticked box; false is
// never sent (trading-api reads the flag as === true and writes the run's stored stop-loss itself, never a browser value)
const applyPayload = ({ id, applyStoploss } = {}) => (applyStoploss === true ? { id, applystoploss: true } : { id });

// The toast text of an answered apply; a repeated apply is a success that changed nothing
const applyResultText = outcome => {
	if (!outcome || outcome.ok !== true) {
		return String(outcome?.error || 'The suggestion was not applied');
	}
	if (outcome.data?.alreadyApplied === true) {
		return 'Already applied: nothing changed';
	}
	return 'Applied: the daily result is being recomputed';
};

const DAY_MS = 86400000;

// The start (00:00 UTC) of the UTC day a millisecond value falls on; the engine pins its candle windows to it
const utcDayStart = ms => Math.floor(ms / DAY_MS) * DAY_MS;

const LATER_WINDOW_NOTE =
	'The daily result was computed on a later daily window than this run, so the numbers can differ.';
const MAYBE_LATER_WINDOW_NOTE =
	'The numbers may differ if the recompute ran after 00:00 UTC, on a later daily window than this run.';
const CHANGED_AFTER_APPLY_NOTE =
	'The strategy was changed after this Apply; its daily result no longer belongs to this suggestion.';

// The line under an applied run: the strategy's daily result as the daemon recomputed it after the Apply, next to the
// suggested one. Nothing is computed here: both numbers are stored values compared in cents. The recomputed result
// lives on the saved strategy (strategyresult is null from the Apply until the daemon has stored a new one) and stores
// no window, so a note says when the daily window is certainly (applied on a later UTC day than the run's window) or
// possibly later than the run's. null for a run that was never applied or without a saved strategy.
// state: recomputing | matches | differs | unknown (no suggested number to compare with) | changed
// Two more fields for a run with its own stop-loss (v1.1), derived from the saved strategy against runstoploss, never
// from a stored flag: stoplossLine ('Stop-loss set to 15%' / 'Stop-loss kept at 3.3%' / 'Stop-loss kept off', null once
// the strategy was changed after the Apply, for a v1.0 run and when the saved stop-loss is not a number) and
// stoplossNote, only in the differs state while the strategy's stop-loss is not the run's: it replaces both window
// notes, also when the recompute certainly ran on a later window, because the stop-loss is the cause we can name.
// A v1.1 patch never holds '/stoplosspercent', so a hand edit of the stop-loss after the Apply is detected against
// metadata.strategyStoploss, the strategy's own value when the run started: a saved value equal to neither the run's
// nor that start value was edited after the Apply, and the run reads 'changed'. Without that metadata nothing changes.
const recomputeView = ({ run, savedStrategy } = {}) => {
	if (!run || !run.appliedat || !savedStrategy) {
		return null;
	}
	const changes = Array.isArray(run.patch?.changes) ? run.patch.changes : [];
	const stillApplied = changes.every(
		change => typeof change?.path !== 'string' || isEqual(getAtPointer(savedStrategy, change.path), change.to),
	);
	const runStop = runStoploss(run);
	const own = strategyStoplossValue(savedStrategy.stoplosspercent);
	const startedWith = strategyStoplossValue(run.metadata?.strategyStoploss);
	const editedAfterApply =
		runStop !== null && own !== null && own !== runStop && startedWith !== null && startedWith !== own;
	if (!stillApplied || editedAfterApply) {
		return {
			state: 'changed',
			text: CHANGED_AFTER_APPLY_NOTE,
			windowNote: null,
			stoplossLine: null,
			stoplossNote: null,
		};
	}
	const ownText = strategyStoplossText(own);
	const stoplossKnown = runStop !== null && ownText !== null;
	let stoplossLine = null;
	if (stoplossKnown) {
		if (own === runStop) {
			stoplossLine = `Stop-loss set to ${formatPercent(runStop)}`;
		} else {
			stoplossLine = own === 0 ? 'Stop-loss kept off' : `Stop-loss kept at ${ownText}`;
		}
	}
	const appliedMs = Date.parse(run.appliedat);
	const cutoffMs = run.metadata?.window?.cutoffMs;
	const laterWindow = Number.isFinite(appliedMs) && isNumber(cutoffMs) && utcDayStart(appliedMs) > cutoffMs;
	const recomputed = formatProfit(savedStrategy.strategyresult?.result?.dailyProfit);
	if (recomputed === null) {
		return {
			state: 'recomputing',
			text: 'Recomputed daily result: recomputing…',
			windowNote: laterWindow ? LATER_WINDOW_NOTE : null,
			stoplossLine,
			stoplossNote: null,
		};
	}
	const suggested = formatProfit(run.stats?.dailyProfit);
	if (suggested === null) {
		return {
			state: 'unknown',
			text: `Recomputed daily result: ${recomputed}/day`,
			windowNote: laterWindow ? LATER_WINDOW_NOTE : null,
			stoplossLine,
			stoplossNote: null,
		};
	}
	const differs = profitCents(recomputed) !== profitCents(suggested);
	let windowNote = null;
	let stoplossNote = null;
	if (differs && stoplossKnown && own !== runStop) {
		stoplossNote = `The strategy kept its own stop-loss (${ownText}) while this run was searched at ${formatPercent(
			runStop,
		)}, so the recomputed result can differ.`;
	} else if (laterWindow) {
		windowNote = LATER_WINDOW_NOTE;
	} else if (differs) {
		windowNote = MAYBE_LATER_WINDOW_NOTE;
	}
	return {
		state: differs ? 'differs' : 'matches',
		text: `Recomputed daily result: ${recomputed}/day (suggested ${suggested}/day)`,
		windowNote,
		stoplossLine,
		stoplossNote,
	};
};

const FUNNEL_LABELS = {
	evaluated: 'Candidates evaluated',
	zeroStoploss: '… with 0 stop-losses',
	zeroBadTrades: '… and 0 bad trades',
	goodTrades20: '… and at least 20 good trades',
	beatsBaseline: '… and beating the baseline',
	rsiCheckPassed: '… and passing the neighbour check',
};

// The stages are cumulative: each count includes the stages before it. A funnel stored before the 0 bad trades rule has
// no zeroBadTrades stage: that row is left out then, so an older not_found run shows the five stages it always had
const funnelRows = funnel => {
	if (!funnel || typeof funnel !== 'object') {
		return [];
	}
	return Object.keys(FUNNEL_LABELS)
		.filter(key => key !== 'zeroBadTrades' || isNumber(funnel.zeroBadTrades))
		.map(key => ({
			key,
			label: FUNNEL_LABELS[key],
			count: isNumber(funnel[key]) ? funnel[key] : null,
		}));
};

// Why a run could not qualify at all (the daemon's own text); null when there is none
const limitationsText = funnel => {
	const list = Array.isArray(funnel?.limitations)
		? funnel.limitations.filter(item => typeof item === 'string' && item)
		: [];
	return list.length > 0 ? list.join('\n') : null;
};

// A failed run: the readable error, else the finish reason label, else the raw code
const failureText = run => {
	const error = String(run?.error ?? '').trim();
	return error || finishReasonText(run?.finishreason);
};

const textOrNull = value => (typeof value === 'string' && value !== '' ? value : null);

// The AI's (untrusted) text apart from the engine's numbers: a label that says where it comes from, the summary, and the
// reason of one setting -- paired through explanation.changes[i].path, else reasons[i] by the index in the patch
const explanationView = (explanation, patchChanges) => {
	if (!explanation || typeof explanation !== 'object') {
		return null;
	}
	let label;
	if (explanation.source === 'ai') {
		label = explanation.model ? `AI commentary (model ${explanation.model})` : 'AI commentary';
	} else if (explanation.source === 'generated') {
		label = 'Generated without AI';
	} else {
		label = `Commentary (source ${String(explanation.source ?? 'not stored')})`;
	}
	const pairs = Array.isArray(explanation.changes) ? explanation.changes : [];
	const reasons = Array.isArray(explanation.reasons) ? explanation.reasons : [];
	const patch = Array.isArray(patchChanges) ? patchChanges : [];
	return {
		label,
		summary: typeof explanation.summary === 'string' ? explanation.summary : '',
		reasonFor: path => {
			const pair = pairs.find(item => item?.path === path);
			if (pair) {
				return textOrNull(pair.reason);
			}
			const index = patch.findIndex(item => item?.path === path);
			return index === -1 ? null : textOrNull(reasons[index]);
		},
	};
};

// The run metadata the user needs, from named keys only (jsonb reorders keys: never iterate metadata)
const metadataRows = metadata => {
	if (!metadata || typeof metadata !== 'object') {
		return [];
	}
	let advisor = null;
	if (metadata.advisor) {
		advisor = metadata.model ? `${metadata.model} (${metadata.advisor})` : String(metadata.advisor);
	}
	const budget = metadata.budget;
	return [
		{ label: 'Window', value: windowText(metadata.window) },
		{ label: 'Model (advisor)', value: advisor },
		{ label: 'Engine version', value: metadata.engineVersion ? String(metadata.engineVersion) : null },
		{
			label: 'Threads',
			value: isNumber(metadata.threadsUsed)
				? `${metadata.threadsUsed} of ${isNumber(metadata.threadsConfigured) ? metadata.threadsConfigured : '?'}`
				: null,
		},
		{ label: 'Rounds', value: isNumber(budget?.maxRounds) ? String(budget.maxRounds) : null },
		{
			label: 'Time budget',
			value: isNumber(budget?.budgetSec) ? `${Math.round((budget.budgetSec / 60) * 10) / 10} min` : null,
		},
	].filter(row => row.value !== null);
};

// A later-round AI failure ends the run with the best result so far: a note, not an error
const advisorFailureNote = metadata => {
	const failure = metadata?.advisorFailure;
	if (!failure || typeof failure !== 'object') {
		return null;
	}
	const where = isNumber(failure.round) ? ` in round ${failure.round}` : '';
	return `The AI stopped answering${where}; the best result found until then was kept.`;
};

// ---- the round trace: level 1 (a row per round) and level 2 (the proposals of one round) ----

const REJECTION_CODES = [
	'unknown_setting',
	'wrong_type',
	'non_finite',
	'out_of_range',
	'bad_choice',
	'stoploss_off_grid',
	'stoploss_required',
	'duplicate_path',
	'too_many_changes',
	'empty',
	'too_many_proposals',
	'out_of_order',
	'setting_fixed',
];

const OUTCOME_LABELS = {
	evaluated: 'evaluated',
	duplicate: 'duplicate of a config already tried',
	panicked: 'the engine failed on it',
	not_evaluated: 'not evaluated (out of time)',
};
REJECTION_CODES.forEach(code => {
	OUTCOME_LABELS[code] = `rejected: ${code.replace(/_/g, ' ')}`;
});
OUTCOME_LABELS.setting_fixed = 'rejected: changes a fixed setting';

// Never blank: an outcome this tab has no label for is shown as the code the daemon stored
const outcomeLabel = code => {
	if (has(OUTCOME_LABELS, code)) {
		return OUTCOME_LABELS[code];
	}
	return code ? String(code) : 'outcome not stored';
};

// One light row per round. bestDailyProfit is the best of ANY candidate of that round (not the run's best qualifier);
// a round without it shows a dash, never 0
const traceRows = roundtrace => {
	const rounds = Array.isArray(roundtrace?.rounds) ? roundtrace.rounds : [];
	return rounds.map(round => {
		const best = formatProfit(round?.bestDailyProfit);
		return {
			round: round?.round,
			tested: isNumber(round?.tested) ? round.tested : null,
			qualifiers: isNumber(round?.qualifiers) ? round.qualifiers : null,
			skipped: round?.skipped === true,
			bestText: best === null ? DASH : `best candidate ${best}/day`,
			proposalCount: Array.isArray(round?.proposals) ? round.proposals.length : 0,
		};
	});
};

// The proposals of ONE round with the changes in the form's labels. The trace stores no 'from', only the new value; the
// reason is the AI's (untrusted, clipped) text and stays a plain string. The keys are for the list rendering
const proposalRows = (round, strategy) => {
	const proposals = Array.isArray(round?.proposals) ? round.proposals : [];
	return proposals.map((proposal, index) => {
		const best = formatProfit(proposal?.best);
		const reason = typeof proposal?.reason === 'string' && proposal.reason.trim() !== '' ? proposal.reason : null;
		const changes = Array.isArray(proposal?.changes) ? proposal.changes : [];
		return {
			key: `proposal-${index}`,
			outcomeText: outcomeLabel(proposal?.outcome),
			qualified: proposal?.qualified === true,
			bestText: best === null ? null : `${best}/day`,
			reason,
			changes: changes.map((change, changeIndex) => {
				const { label, raw } = changeLabel(change?.path, strategy);
				return { key: `change-${changeIndex}`, label, title: raw, to: formatValue(change?.to) };
			}),
		};
	});
};

module.exports = {
	POLL_INTERVAL_MS,
	PAGE_SIZE,
	MAX_LOADED_RUNS,
	RUN_STOPLOSS_CHOICES,
	DEFAULT_RUN_STOPLOSS,
	parseRunStoploss,
	formatPercent,
	startToastText,
	V10_GRID_LABEL,
	runStoploss,
	runStoplossLabel,
	V10_LIST_LABEL,
	runStoplossListLabel,
	strategyStoplossValue,
	strategyStoplossText,
	startHint,
	runStoplossHeaderText,
	ACTIVE_STATUSES,
	STATUS_LABELS,
	STATUS_PILL_CLASSES,
	PILL_BASE_CLASS,
	statusView,
	isActiveRun,
	hasActiveRun,
	profitCents,
	formatProfit,
	formatClock,
	formatMaxMinutes,
	progressView,
	compareRuns,
	mergeFirstPage,
	createRunPoller,
	RESULT_KEYS,
	unsavedEdits,
	appendRunPage,
	hasMorePages,
	FINISH_REASON_LABELS,
	finishReasonText,
	outcomeText,
	rowBadges,
	BADGE_CLASSES,
	appliabilitySignature,
	cancelNeedsConfirm,
	CANCEL_RUNNING_CONFIRM,
	UNSAVED_EDITS_TOAST,
	PASS_MARK_LABELS,
	passMarks,
	statsRows,
	stabilityText,
	utcText,
	windowText,
	baselineNote,
	EARLIER_RULE_NOTE,
	earlierRuleNote,
	storedResultNote,
	FUNNEL_LABELS,
	funnelRows,
	limitationsText,
	failureText,
	explanationView,
	metadataRows,
	advisorFailureNote,
	OUTCOME_LABELS,
	outcomeLabel,
	traceRows,
	proposalRows,
	HYPOTHESIS_TEXT,
	APPLIABLE_REASON_TEXTS,
	appliableText,
	applyConfirmText,
	applyResultText,
	applyCheckboxView,
	applyPayload,
	utcDayStart,
	recomputeView,
};
